import React, { useState, useEffect, useCallback } from 'react'
import { Typography, Button, Card, Spin, Progress } from 'antd'
import { CheckCircleTwoTone, LoadingOutlined, ArrowRightOutlined } from '@ant-design/icons'
import IntlMessages from '../../../../util/IntlMessages'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import upperFirst from 'lodash/upperFirst'
import { openChat } from '../../../../util/set-crisp-session-info'

const { Title, Paragraph, Text } = Typography

interface SummaryStepProps {
  onLaunch: () => void
  usersCount: number
  authCompanyPlatform: PlatformEnum
}

const LOADING_STORAGE_KEY = 'adp_onboarding_loading'
export const LOADING_COMPLETED_KEY = 'adp_onboarding_completed'

export const SummaryStep: React.FC<SummaryStepProps> = ({ onLaunch, usersCount, authCompanyPlatform }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [totalSeconds, setTotalSeconds] = useState(0)

  const initializeTimer = useCallback(() => {
    const isCompleted = localStorage.getItem(LOADING_COMPLETED_KEY)

    if (isCompleted) {
      return
    }

    const savedTimer = localStorage.getItem(LOADING_STORAGE_KEY)

    if (savedTimer) {
      const { endTime, totalSeconds: savedTotalSeconds }: { endTime: number; totalSeconds?: number } = JSON.parse(savedTimer)
      const now = Date.now()
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000))

      if (remaining > 0) {
        setIsLoading(true)
        setRemainingSeconds(remaining)
        setTotalSeconds(typeof savedTotalSeconds === 'number' ? savedTotalSeconds : remaining)
      } else {
        localStorage.removeItem(LOADING_STORAGE_KEY)
        localStorage.setItem(LOADING_COMPLETED_KEY, 'true')
      }
    } else {
      // Timer duration: minimum 180 seconds, or usersCount seconds if higher
      // If usersCount is 0 (users were skipped), still use 180 seconds for setup time
      const timerDuration = Math.max(180, usersCount || 0)
      const endTime = Date.now() + timerDuration * 1000

      localStorage.setItem(
        LOADING_STORAGE_KEY,
        JSON.stringify({
          endTime,
          totalSeconds: timerDuration,
        })
      )

      setIsLoading(true)
      setRemainingSeconds(timerDuration)
      setTotalSeconds(timerDuration)
    }
  }, [])

  useEffect(() => {
    initializeTimer()
  }, [initializeTimer])

  useEffect(() => {
    if (!isLoading || remainingSeconds <= 0) {
      return
    }

    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          setIsLoading(false)
          localStorage.removeItem(LOADING_STORAGE_KEY)
          localStorage.setItem(LOADING_COMPLETED_KEY, 'true')
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isLoading])

  if (isLoading) {
    return (
      <div className="summary-step-container">
        <div className="summary-step-loading">
          <Spin indicator={<LoadingOutlined className="summary-step-loading-icon" spin />} size="large" />
          <Title level={2} className="summary-step-loading-title">
            <IntlMessages id="onboarding.adp.summaryStep.loading.title" />
          </Title>
          <Text className="summary-step-loading-text">
            <IntlMessages id="onboarding.adp.summaryStep.loading.pleaseWait" />
          </Text>
          {remainingSeconds > 0 && (
            <div className="summary-step-timer-container">
              <Progress
                percent={totalSeconds > 0 ? Math.round(((totalSeconds - remainingSeconds) / totalSeconds) * 100) : 0}
                status="active"
                showInfo={false}
                className="summary-step-progress"
              />
              <Text className="summary-step-timer-text">
                <IntlMessages
                  id="onboarding.adp.summaryStep.loading.subtitle"
                  values={{
                    count: `${Math.floor(remainingSeconds / 60)}:${(remainingSeconds % 60).toString().padStart(2, '0')}`,
                  }}
                />
              </Text>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="summary-step-container">
      <Title level={2} className="summary-step-title">
        <IntlMessages id="onboarding.adp.summaryStep.title" />
        <span className="summary-step-party-emoji" role="img" aria-label="party">
          🎉
        </span>
      </Title>
      <Paragraph className="summary-step-description">
        <IntlMessages id="onboarding.adp.summaryStep.subtitle" />
      </Paragraph>
      <Card className="summary-step-success-box">
        <Title level={4} className="summary-step-success-title">
          <IntlMessages id="onboarding.adp.summaryStep.whatWeSetup" />
        </Title>
        <ul className="summary-step-checklist">
          {usersCount > 0 && (
            <li>
              <CheckCircleTwoTone twoToneColor="#52c41a" className="summary-step-check-icon" />
              <Text>
                <IntlMessages id="onboarding.adp.summaryStep.usersImported" values={{ count: usersCount }} />
              </Text>
            </li>
          )}
          {authCompanyPlatform !== PlatformEnum.Email && (
            <li>
              <CheckCircleTwoTone twoToneColor="#52c41a" className="summary-step-check-icon" />
              <Text>
                <IntlMessages id="onboarding.adp.summaryStep.platformConnected" values={{ platform: upperFirst(authCompanyPlatform) }} />
              </Text>
            </li>
          )}
          <li>
            <CheckCircleTwoTone twoToneColor="#52c41a" className="summary-step-check-icon" />
            <Text>
              <IntlMessages id="onboarding.adp.summaryStep.balancesSynced" />
            </Text>
          </li>
          <li>
            <CheckCircleTwoTone twoToneColor="#52c41a" className="summary-step-check-icon" />
            <Text>
              <IntlMessages id="onboarding.adp.summaryStep.approvalWorkflows" />
            </Text>
          </li>
        </ul>
      </Card>
      <Card className="summary-step-tip-box">
        <span className="summary-step-tip-emoji" role="img" aria-label="bulb">
          💡
        </span>
        <Text>
          <IntlMessages id="onboarding.adp.summaryStep.proTip" />
        </Text>
        <Button onClick={() => openChat()} type="link" className="summary-step-tip-action">
          <IntlMessages id="onboarding.adp.summaryStep.scheduleCall" />
        </Button>
      </Card>
      <Button type="primary" size="large" className="summary-step-launch-btn" onClick={onLaunch}>
        <IntlMessages id="onboarding.adp.summaryStep.launchButton" /> <ArrowRightOutlined />
      </Button>
    </div>
  )
}
